import { registrationSchema, type Registration } from '@pgs/shared';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chrome } from '@/components/layout/Chrome';
import { api, extractValidationIssues } from '@/lib/api';
import { useApplicantStore } from '@/features/applicant/applicantStore';
import { NavButtons } from './NavButtons';
import {
  summariseAgency,
  summariseBank,
  summariseCisAssessment,
  summariseCisVerification,
  summariseDocuments,
  summariseOccupation,
  summarisePayment,
  summarisePersonal,
  summariseReferral,
  summariseUtr,
} from './reviewSummary';
import { StepPanel } from './StepPanel';
import { Stepper, type StepDef } from './Stepper';
import { useWizard, visibleStepIds } from './store';
import { Step0Personal } from './steps/Step0Personal';
import { Step1Referral } from './steps/Step1Referral';
import { Step2Agency } from './steps/Step2Agency';
import { Step3Bank } from './steps/Step3Bank';
import { Step4Occupation } from './steps/Step4Occupation';
import { Step5Utr } from './steps/Step5Utr';
import { Step6Payment } from './steps/Step6Payment';
import { Step7Documents } from './steps/Step7Documents';
import { Step8CisAssessment } from './steps/Step8CisAssessment';
import { Step9Review, type ReviewRow } from './steps/Step9Review';

const STEP_DEFS: Record<string, StepDef> = {
  personal: { id: 'personal', label: 'Personal Details', icon: 'fa-user' },
  referral: { id: 'referral', label: 'Refer A Friend', icon: 'fa-users' },
  agency: { id: 'agency', label: 'Your Agency', icon: 'fa-home' },
  bank: { id: 'bank', label: 'Bank Details', icon: 'fa-university' },
  occupation: { id: 'occupation', label: 'Occupation', icon: 'fa-briefcase' },
  utr: { id: 'utr', label: 'UTR', icon: 'fa-id-card' },
  payment: { id: 'payment', label: 'New Starter Information', icon: 'fa-pound-sign' },
  documents: { id: 'documents', label: 'Your Documents', icon: 'fa-file-alt' },
  'cis-assessment': { id: 'cis-assessment', label: 'CIS Assessment', icon: 'fa-hard-hat' },
  review: { id: 'review', label: 'Final Checks', icon: 'fa-check-circle' },
};

export const RegistrationWizard = () => {
  const navigate = useNavigate();
  const { stepIndex, draft, setStep, patch, reset } = useWizard();
  const authEmail = useApplicantStore((s) => s.email ?? s.pendingEmail);

  const ids = useMemo(() => visibleStepIds(draft.cis), [draft.cis]);
  const steps = useMemo(() => ids.map((id) => STEP_DEFS[id]!), [ids]);
  const currentId = ids[stepIndex] ?? 'personal';

  const submitMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(draft);
      const parsed = registrationSchema.safeParse(payload);
      if (!parsed.success) {
        throw Object.assign(new Error('Draft incomplete'), { issues: parsed.error.issues });
      }
      return (await api.post<{ id: string }>('/registrations', parsed.data)).data;
    },
    onSuccess: () => {
      reset();
      navigate('/register/thank-you');
    },
  });

  const go = (nextId: string) => {
    const idx = ids.indexOf(nextId);
    setStep(idx === -1 ? stepIndex : idx);
  };

  const reviewRows = useMemo<ReviewRow[]>(
    () => [
      {
        id: 'personal',
        title: 'Personal Details',
        fields: summarisePersonal(draft.personal),
        jumpTo: ids.indexOf('personal'),
      },
      {
        id: 'referral',
        title: 'Refer A Friend Code',
        fields: summariseReferral(draft.referral),
        jumpTo: ids.indexOf('referral'),
      },
      {
        id: 'agency',
        title: 'Your Agency',
        fields: summariseAgency(draft.agency),
        jumpTo: ids.indexOf('agency'),
      },
      {
        id: 'bank',
        title: 'Your Bank Details',
        fields: summariseBank(draft.bank),
        jumpTo: ids.indexOf('bank'),
      },
      {
        id: 'occupation',
        title: 'Your Occupation',
        fields: summariseOccupation(draft.occupation),
        jumpTo: ids.indexOf('occupation'),
      },
      ...(draft.cis
        ? [
            {
              id: 'cis-assessment',
              title: 'CIS Self-Employed Assessment',
              fields: summariseCisAssessment(draft.cisAssessment),
              jumpTo: ids.indexOf('cis-assessment'),
            },
            {
              id: 'utr',
              title: 'Your UTR',
              fields: summariseUtr(draft.utr),
              jumpTo: ids.indexOf('utr'),
            },
            {
              id: 'cis-verification',
              title: 'CIS HMRC Verification',
              fields: summariseCisVerification(draft.cisVerification),
              jumpTo: ids.indexOf('utr'),
            },
          ]
        : []),
      {
        id: 'payment',
        title: 'New Starter Information',
        fields: summarisePayment(draft.payment),
        jumpTo: ids.indexOf('payment'),
      },
      {
        id: 'documents',
        title: 'Your Documents',
        fields: summariseDocuments(draft.documents),
        jumpTo: ids.indexOf('documents'),
      },
    ],
    [draft, ids],
  );

  return (
    <Chrome>
      <Stepper
        steps={steps}
        current={stepIndex}
        onJump={(i) => {
          if (i <= stepIndex) setStep(i);
        }}
      />
      <div className="min-h-[60vh] bg-[#edf0f5] py-9 px-5">
        {currentId === 'personal' && (
          <Step0Personal
            defaultValues={draft.personal}
            authEmail={authEmail}
            onSubmit={(values) => {
              patch('personal', values);
              go('referral');
            }}
          />
        )}
        {currentId === 'referral' && (
          <Step1Referral
            defaultValues={draft.referral}
            onBack={() => setStep(stepIndex - 1)}
            onSubmit={(values) => {
              patch('referral', values);
              go('agency');
            }}
          />
        )}
        {currentId === 'agency' && (
          <Step2Agency
            defaultValues={draft.agency}
            onBack={() => setStep(stepIndex - 1)}
            onSubmit={(values) => {
              patch('agency', values);
              go('bank');
            }}
          />
        )}
        {currentId === 'bank' && (
          <Step3Bank
            defaultValues={draft.bank}
            onBack={() => setStep(stepIndex - 1)}
            onSubmit={(values) => {
              patch('bank', values);
              go('occupation');
            }}
          />
        )}
        {currentId === 'occupation' && (
          <Step4Occupation
            defaultValues={draft.occupation}
            onBack={() => setStep(stepIndex - 1)}
            onSubmit={(values) => {
              patch('occupation', { ...values, cis: values.cis });
              patch('cis', values.cis);
              go('payment');
            }}
          />
        )}
        {currentId === 'utr' && (
          <Step5Utr
            defaultValues={draft.utr}
            personal={draft.personal}
            verification={draft.cisVerification}
            onBack={() => setStep(stepIndex - 1)}
            onSubmit={(values) => {
              patch('utr', values);
              go('documents');
            }}
            onVerified={(v) => patch('cisVerification', v)}
          />
        )}
        {currentId === 'payment' && (
          <Step6Payment
            defaultValues={draft.payment}
            onBack={() => setStep(stepIndex - 1)}
            onSubmit={(values) => {
              patch('payment', values);
              go(draft.cis ? 'cis-assessment' : 'documents');
            }}
          />
        )}
        {currentId === 'documents' && (
          <Step7Documents
            initial={draft.documents}
            initialDob={draft.personal.dob}
            onBack={() => setStep(stepIndex - 1)}
            onChange={(docs) => patch('documents', docs)}
            onSubmit={(docs) => {
              patch('documents', docs);
              go('review');
            }}
          />
        )}
        {currentId === 'cis-assessment' && (
          <Step8CisAssessment
            defaultValues={draft.cisAssessment as never}
            personal={draft.personal}
            occupation={draft.occupation}
            onBack={() => setStep(stepIndex - 1)}
            onSubmit={(values) => {
              patch('cisAssessment', values as never);
              go('utr');
            }}
          />
        )}
        {currentId === 'review' && (
          <>
            <Step9Review
              rows={reviewRows}
              onEdit={(idx) => setStep(idx)}
              onBack={() => setStep(stepIndex - 1)}
              submitting={submitMutation.isPending}
              onSubmit={() => submitMutation.mutate()}
              declarationAccepted={draft.declarationAccepted}
              onDeclarationChange={(v) => patch('declarationAccepted', v)}
            />
            {submitMutation.isError && (
              <SubmissionErrors
                issues={
                  (submitMutation.error as { issues?: Array<{ path: unknown[]; message: string }> })
                    .issues ?? extractValidationIssues(submitMutation.error)
                }
                stepIds={ids}
                onJump={(stepId) => go(stepId)}
              />
            )}
          </>
        )}
      </div>
    </Chrome>
  );
};

interface Issue {
  path: unknown[] | string;
  message: string;
}

// Maps the top-level key in a Zod issue path to the wizard step it belongs to.
const FIELD_STEP: Record<string, string> = {
  personal: 'personal',
  referral: 'referral',
  agency: 'agency',
  bank: 'bank',
  occupation: 'occupation',
  cisAssessment: 'cis-assessment',
  utr: 'utr',
  payment: 'payment',
  documents: 'documents',
  declarationAccepted: 'review',
  cis: 'occupation',
};

// Friendly names for leaf fields so users see "Email" rather than "personal.email".
const FIELD_LABEL: Record<string, string> = {
  // personal
  title: 'Title',
  gender: 'Gender',
  forename: 'Forename',
  middleName: 'Middle name',
  surname: 'Last name',
  preferredName: 'Preferred name',
  mobile: 'Mobile number',
  email: 'Email',
  dob: 'Date of birth',
  nationality: 'Nationality',
  niNumber: 'National Insurance number',
  preferredContact: 'Preferred contact method',
  address: 'Address',
  line1: 'Address line 1',
  line2: 'Address line 2',
  city: 'Town / city',
  postcode: 'Postcode',
  country: 'Country',
  // referral
  hasCode: 'Referral choice',
  code: 'Referral code',
  // agency
  branch: 'Branch',
  consultantName: 'Consultant name',
  consultantEmail: 'Consultant email',
  consultantMobile: 'Consultant mobile',
  // bank
  bankName: 'Bank / building society',
  accountName: 'Account name',
  accountNumber: 'Account number',
  sortCode: 'Sort code',
  // occupation
  sector: 'Sector',
  role: 'Job role',
  startDate: 'Start date',
  // payment
  situation: 'Employment situation',
  // documents
  photoId: 'Photo ID',
  proofAddress: 'Proof of address',
  rightToWork: 'Right to work',
  niProof: 'NI number proof',
  nameChange: 'Name change document',
  evisa: 'eVisa share code',
  // utr
  mode: 'UTR option',
  number: 'UTR number',
  // cis assessment
  jobTitle: 'Job title',
  duration: 'Duration in current role',
  qualifications: 'Qualifications',
  roleDescription: 'Role description',
  assignmentsInLast12Months: 'Assignments in last 12 months',
  employmentBenefits: 'Employment benefits',
  submitsSelfAssessment: 'Self-assessment filing',
  coversOwnExpenses: 'Covers own expenses',
  canDeclineWork: 'Can decline work',
  multipleClients: 'Multiple clients',
  decidesHowWorkDone: 'Decides how work is done',
  subjectToSupervision: 'Subject to supervision',
  understandsSubstitution: 'Understands substitution',
  providesOwnEquipment: 'Provides own equipment',
  equipmentDetails: 'Equipment details',
  receivesTraining: 'Receives training',
  trainingDetails: 'Training details',
  fullName: 'Full name',
  date: 'Date',
  contactNumber: 'Contact number',
  declarationAccepted: 'Declaration',
};

const normalisePath = (p: Issue['path']): string[] => {
  if (Array.isArray(p)) return p.map(String);
  return String(p ?? '')
    .split('.')
    .filter(Boolean);
};

const STEP_TITLE: Record<string, string> = Object.fromEntries(
  Object.entries(STEP_DEFS).map(([id, def]) => [id, def.label]),
);

const friendlyIssue = (
  issue: Issue,
): { stepId: string | undefined; sectionLabel: string; fieldLabel: string } => {
  const parts = normalisePath(issue.path);
  if (parts.length === 0) {
    return { stepId: undefined, sectionLabel: 'Your application', fieldLabel: '' };
  }
  const top = parts[0]!;
  const stepId = FIELD_STEP[top];
  const sectionLabel = stepId ? (STEP_TITLE[stepId] ?? top) : top;
  const leaf = parts[parts.length - 1]!;
  const fieldLabel =
    FIELD_LABEL[leaf] ?? (parts.length > 1 ? parts.slice(1).join(' › ') : '');
  return { stepId, sectionLabel, fieldLabel };
};

interface SubmissionErrorsProps {
  issues: Issue[];
  stepIds: string[];
  onJump: (stepId: string) => void;
}

const SubmissionErrors = ({ issues, stepIds, onJump }: SubmissionErrorsProps) => (
  <div className="mx-auto mt-4 max-w-[960px] rounded border-l-4 border-red-500 bg-red-50 p-4">
    <p className="mb-2 text-sm font-bold text-red-700">
      Please fix the following before submitting:
    </p>
    {issues.length === 0 ? (
      <p className="text-xs text-red-700">
        Some required information is missing. Please review each section above and try again.
      </p>
    ) : (
      <ul className="space-y-1.5 text-xs text-red-700">
        {issues.map((i, idx) => {
          const { stepId, sectionLabel, fieldLabel } = friendlyIssue(i);
          const jumpTo = stepId && stepIds.includes(stepId) ? stepId : undefined;
          return (
            <li key={idx} className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                <strong>
                  {sectionLabel}
                  {fieldLabel ? ` — ${fieldLabel}` : ''}:
                </strong>{' '}
                {i.message}
              </span>
              {jumpTo && (
                <button
                  type="button"
                  onClick={() => onJump(jumpTo)}
                  className="rounded border border-red-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700 hover:bg-red-100"
                >
                  Fix ›
                </button>
              )}
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

// Convert the loose draft into the strict discriminated-union payload the API expects.
const buildPayload = (draft: ReturnType<typeof useWizard.getState>['draft']): unknown => {
  const base = {
    personal: draft.personal,
    referral: draft.referral,
    agency: draft.agency,
    bank: draft.bank,
    occupation: { ...draft.occupation, cis: draft.cis },
    payment: draft.payment,
    documents: draft.documents,
    declarationAccepted: draft.declarationAccepted,
  };
  if (draft.cis) {
    return {
      ...base,
      cis: true,
      utr: draft.utr,
      cisAssessment: draft.cisAssessment,
      cisVerification: draft.cisVerification?.result,
    } as Registration;
  }
  return { ...base, cis: false } as Registration;
};

// Fallback — keep imports used so tree-shaking still works.
void NavButtons;
void StepPanel;
